import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { v4 as uuidv4 } from 'uuid';
import { IDeliverableConfig, TDeliverable } from '../deliverable-list.interface';
import { VisualComponent } from './visual.component';
import { EVisualFocus, IVisualDeliverableForm } from './visual.interface';

@Injectable({
  providedIn: 'root',
})
export class VisualService {
  private readonly translate = inject(TranslateService);

  public getConfig(): IDeliverableConfig {
    return {
      component: VisualComponent,
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<IVisualDeliverableForm> | null => {
        if (item && item.type !== EDeliverableType.visual) {
          return null;
        }

        const mockFile = item?.imageId && !item?.imageFile ? FileUploaderService.createCustomFile(item.imageId) : null;

        const form = new FormGroup({
          customCtaValues: new FormControl<ICustomCtaValue[]>(item?.customCtaValues ?? []),
          exclusiveTo: new FormControl<string | null>(item?.exclusiveTo || creatorId || null),
          focus: new FormControl<EVisualFocus | null>(item?.focus ?? EVisualFocus.mixed),
          globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(item?.globalCtaValues ?? null),
          id: new FormControl<string | null>(item?.id ?? uuidv4(), Validators.required),
          imageFile: new FormControl<ICustomFile | null>(
            mockFile ?? item?.imageFile ?? null,
            isViewMode ? [] : [Validators.required, imageExtensionValidator()],
          ),
          imageId: new FormControl<string | null>(item?.imageId ?? null),
          isNew: new FormControl<boolean>(!item?.id),
          name: new FormControl<string | null>(
            item?.name ??
              this.translate.instant('pages.campaignForm.ctaDialog.ctaDetails.deliverables.visualDeliverable.title'),
            [Validators.required, Validators.maxLength(255)],
          ),
          sponsoredContentRelevant: new FormControl<boolean>(item?.sponsoredContentRelevant ?? true, {
            nonNullable: true,
          }),
          type: new FormControl<EDeliverableType.visual>(EDeliverableType.visual, { nonNullable: true }),
        }) as FormGroup<IVisualDeliverableForm>;

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
